import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Phone } from 'lucide-react';
import { NisanIcon } from '../components/icons/NisanIcon';
import { requireAuth } from '../components/AuthGate';

export default function LandingPage({ onStartBooking, onViewTracker, onViewPastBurials, onViewAttendedBurials }) {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-[#5EEAD4] text-white py-4 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NisanIcon className="h-6 w-6" strokeWidth={2} />
            <h1 className="text-xl sm:text-2xl font-semibold">Ihsan Casket</h1>
          </div>
          <a href="tel:+6591234567" className="flex items-center gap-2 text-sm sm:text-base hover:opacity-80 transition-opacity">
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">Emergency: 9123 4567</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Main Action Cards */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {/* Flow A: Booking */}
            <Card className="border-2 hover:border-primary hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 sm:p-8">
                <div className="mb-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3">I Need to Arrange a Funeral</h3>
                  <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed">
                    Report a death and book burial services with our guided process
                  </p>
                </div>
                <Button 
                  size="lg" 
                  className="w-full text-base sm:text-lg h-12 sm:h-14"
                  onClick={async () => {
                    if (await requireAuth()) onStartBooking();
                  }}
                >
                  Report a Death
                </Button>
              </CardContent>
            </Card>

            {/* Flow B: Tracker */}
            <Card className="border-2 hover:border-accent hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 sm:p-8">
                <div className="mb-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="h-7 w-7 sm:h-8 sm:w-8 text-accent" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3">I Am Attending a Funeral</h3>
                  <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed">
                    Track the service progress and receive location updates
                  </p>
                </div>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full text-base sm:text-lg h-12 sm:h-14 border-2 hover:bg-accent hover:text-accent-foreground"
                  onClick={() => onViewTracker('funeral-mr-razak')}
                >
                  View Service Tracker
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-muted/50">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-semibold text-center mb-8 sm:mb-10">How We Serve You</h3>
          <ol className="space-y-6 sm:space-y-7">
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                1
              </div>
              <div className="pt-1">
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold">Quick Booking:</span>{' '}
                  <span className="text-muted-foreground">Simple guided process to arrange services in minutes</span>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                2
              </div>
              <div className="pt-1">
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold">Real-Time Updates:</span>{' '}
                  <span className="text-muted-foreground">Track every step of the service with live location</span>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                3
              </div>
              <div className="pt-1">
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold">Community Support:</span>{' '}
                  <span className="text-muted-foreground">Share with loved ones and receive donations</span>
                </p>
              </div>
            </li>
          </ol>

          {/* History Actions */}
          <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <Button
              data-testid="btn-my-past-burials"
              onClick={onViewPastBurials}
              size="lg"
              className="flex-1 h-12 text-base"
            >
              My Past burials
            </Button>
            <Button
              data-testid="btn-my-attended-burials"
              onClick={onViewAttendedBurials}
              variant="outline"
              size="lg"
              className="flex-1 h-12 text-base border-2"
            >
              My Attended burials
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary/5 py-8 px-4 sm:px-6 mt-auto">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-muted-foreground text-sm sm:text-base">
            © 2024 Ihsan Casket. Available 24/7 for Muslim burial services in Singapore.
          </p>
          <p className="text-muted-foreground text-sm mt-2">
            Emergency Hotline: <a href="tel:+6591234567" className="text-primary hover:underline font-medium">9123 4567</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
